import React, { useState, useRef } from 'react';
import { RenovationExpense, ExpenseCategory } from '../../types';
import { parseReceiptImage } from '../../services/aiService';
import { ScanLine, Upload, X, Loader2, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { motion } from 'framer-motion';

interface SmartScanModalProps {
  onSave: (expense: RenovationExpense) => void;
  onClose: () => void;
}

const SmartScanModal: React.FC<SmartScanModalProps> = ({ onSave, onClose }) => {
  const [step, setStep] = useState<'upload' | 'scanning' | 'review'>('upload');
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form Data for Review Step
  const [scannedData, setScannedData] = useState<Partial<RenovationExpense>>({
    description: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    category: ExpenseCategory.Materials
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result as string;
      setPreview(base64String);
      setStep('scanning');
      setError(null);

      try {
        const base64Data = base64String.split(',')[1];
        const result = await parseReceiptImage(base64Data, file.type);
        
        if (result) {
          setScannedData({
             description: result.description || 'Unknown Merchant',
             amount: result.amount || 0,
             date: result.date || new Date().toISOString().split('T')[0],
             category: (result.category as ExpenseCategory) || ExpenseCategory.Materials
          });
          setStep('review');
        } else {
            throw new Error("Could not extract data.");
        }
      } catch (err) {
        console.error(err);
        setError("AI could not read the receipt clearly. Please enter details manually.");
        setStep('review'); 
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files?.[0];
      if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
          processFile(file);
      }
  };

  const handleSave = () => {
    const finalExpense: RenovationExpense = {
        id: Date.now().toString(),
        description: scannedData.description || 'Receipt',
        amount: scannedData.amount || 0,
        date: scannedData.date || new Date().toISOString().split('T')[0],
        category: (scannedData.category as ExpenseCategory) || ExpenseCategory.Materials
    };
    onSave(finalExpense);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-md" onClick={onClose}></div>
      
      <div className="bg-slate-900 w-full max-w-lg rounded-3xl border border-slate-700 shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900 z-10">
            <h3 className="font-bold text-lg text-white flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-emerald-500" /> Smart Scan
            </h3>
            <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
            </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
            
            {/* STEP 1: UPLOAD */}
            {step === 'upload' && (
                <div 
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className="border-2 border-dashed border-slate-700 rounded-2xl h-64 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-800/50 hover:border-emerald-500/50 transition-all group relative overflow-hidden"
                >
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner border border-slate-700">
                        <Upload className="w-8 h-8 text-slate-500 group-hover:text-emerald-400" />
                    </div>
                    <p className="text-slate-300 font-bold mb-1">Upload Receipt</p>
                    <p className="text-xs text-slate-500">Drop image here or click to browse</p>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                </div>
            )}

            {/* STEP 2: SCANNING ANIMATION */}
            {step === 'scanning' && preview && (
                <div className="relative rounded-2xl overflow-hidden h-64 border border-slate-700 bg-black flex items-center justify-center">
                    <img src={preview} alt="Receipt" className="h-full w-full object-contain opacity-50 blur-sm" />
                    
                    {/* Laser Scanner Effect */}
                    <motion.div 
                        initial={{ top: 0 }}
                        animate={{ top: "100%" }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                        className="absolute left-0 right-0 h-0.5 bg-emerald-500 shadow-[0_0_25px_rgba(16,185,129,1)] z-10"
                    />
                    
                    {/* Grid Overlay */}
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.1)_1px,transparent_1px)] bg-[size:20px_20px] opacity-30"></div>

                    <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                         <div className="bg-slate-900/80 backdrop-blur-xl p-4 rounded-2xl border border-slate-700 flex flex-col items-center shadow-2xl">
                             <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-2" />
                             <p className="text-emerald-400 font-mono text-xs uppercase tracking-widest">Processing OCR...</p>
                         </div>
                    </div>
                </div>
            )}

            {/* STEP 3: REVIEW */}
            {step === 'review' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="flex gap-4">
                        {preview && (
                            <div className="h-24 w-24 rounded-xl overflow-hidden border border-slate-800 relative bg-black shrink-0">
                                <img src={preview} alt="Thumb" className="w-full h-full object-cover opacity-80" />
                            </div>
                        )}
                        <div className="flex-1">
                            {error ? (
                                <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl flex items-start gap-2 text-xs h-full">
                                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> 
                                    <span>{error}</span>
                                </div>
                            ) : (
                                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-3 rounded-xl flex items-start gap-2 text-xs h-full">
                                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> 
                                    <span>Scan successful. Please verify the extracted details below.</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Merchant / Description</label>
                            <input 
                                value={scannedData.description}
                                onChange={e => setScannedData({...scannedData, description: e.target.value})}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Total Amount (€)</label>
                                <input 
                                    type="number"
                                    value={scannedData.amount}
                                    onChange={e => setScannedData({...scannedData, amount: Number(e.target.value)})}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-emerald-400 font-bold font-mono outline-none focus:border-emerald-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Date</label>
                                <input 
                                    type="date"
                                    value={scannedData.date}
                                    onChange={e => setScannedData({...scannedData, date: e.target.value})}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white outline-none focus:border-emerald-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
                            <select 
                                value={scannedData.category}
                                onChange={e => setScannedData({...scannedData, category: e.target.value as ExpenseCategory})}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white outline-none focus:border-emerald-500"
                            >
                                {Object.values(ExpenseCategory).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900 flex justify-between items-center">
            {step === 'review' ? (
                 <>
                    <button onClick={() => setStep('upload')} className="text-slate-500 hover:text-white text-sm font-medium">Rescan</button>
                    <button 
                        onClick={handleSave}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-xl font-bold shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2"
                    >
                        <CheckCircle2 className="w-4 h-4" /> Save Expense
                    </button>
                 </>
            ) : (
                <div className="w-full flex justify-center">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1.5 opacity-60">
                        <FileText className="w-3 h-3" /> Powered by Gemini 2.5 Multimodal
                    </span>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default SmartScanModal;